import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { previewKind } from "./clientDocuments.utils";

/**
 * Renders a document inline from a blob. The object URL is revoked when the dialog closes
 * or the document changes, otherwise every preview leaks a blob for the life of the tab.
 */
const DocumentPreviewDialog = ({ open, document, loading, error, blob, onClose, onDownload }) => {
  const [objectUrl, setObjectUrl] = useState("");

  useEffect(() => {
    if (!blob) {
      setObjectUrl("");
      return undefined;
    }
    const url = window.URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => window.URL.revokeObjectURL(url);
  }, [blob]);

  const kind = previewKind(document);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className="flex items-center justify-between gap-2">
        <span className="truncate">{document?.originalFilename || "Preview"}</span>
        <IconButton size="small" onClick={onClose} aria-label="Close preview">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className="min-h-[60vh]">
        {loading && (
          <Box className="flex items-center justify-center h-[60vh]">
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && !error && objectUrl && kind === "pdf" && (
          <iframe
            title={document?.originalFilename || "Document preview"}
            src={objectUrl}
            className="w-full h-[70vh] border-0"
          />
        )}
        {!loading && !error && objectUrl && kind === "image" && (
          <Box className="flex items-center justify-center">
            <img
              src={objectUrl}
              alt={document?.originalFilename || "Document preview"}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </Box>
        )}
        {!loading && !error && kind === "none" && (
          <Typography variant="body2" className="text-app-text-muted">
            This file type cannot be previewed in the browser. Download it to open it.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<DownloadIcon />} onClick={() => onDownload(document)}>
          Download
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
